import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_LANGUAGE,
  LANGUAGE_QUERY_PARAM,
  SUPPORTED_LANGUAGES,
  type SupportedLanguageCode,
} from './languages';
import { consumeSkipUrlLanguageApply, setSkipUrlLanguageApply } from './languageUrlState';

const isSupportedLanguage = (value: string | null): value is SupportedLanguageCode =>
  SUPPORTED_LANGUAGES.some((language) => language.code === value);

/** Applies ?lang= from the URL on external navigation; keeps URL in sync on first load from storage. */
const LanguageUrlSync: React.FC = () => {
  const { i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (consumeSkipUrlLanguageApply()) {
      return;
    }

    const urlLanguage = searchParams.get(LANGUAGE_QUERY_PARAM);
    if (!isSupportedLanguage(urlLanguage)) {
      return;
    }

    const activeLanguage = i18n.resolvedLanguage?.split('-')[0];
    if (activeLanguage !== urlLanguage) {
      void i18n.changeLanguage(urlLanguage);
    }
  }, [searchParams, i18n]);

  useEffect(() => {
    const urlLanguage = searchParams.get(LANGUAGE_QUERY_PARAM);
    const activeLanguage = (i18n.resolvedLanguage?.split('-')[0] ??
      DEFAULT_LANGUAGE) as SupportedLanguageCode;

    if (urlLanguage === activeLanguage) {
      return;
    }

    setSkipUrlLanguageApply();
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set(LANGUAGE_QUERY_PARAM, activeLanguage);
    setSearchParams(nextParams, { replace: true });
  }, [i18n.resolvedLanguage, searchParams, setSearchParams]);

  return null;
};

export default LanguageUrlSync;
